import { PaperAirplaneIcon } from '@heroicons/react/16/solid'
import { useRef, useState } from 'react'
import { Subject } from 'rxjs'
import { Messagebox } from 'View/Common/index.js'
import type { BaseModalProps } from '../../../Modal/BaseModal.js'
import { BaseModal } from '../../../Modal/BaseModal.js'
import { tracingAxios } from '../../../../Repository/Sentry.js'
import { Environment } from '../../../../environment.js'
import { addMessage } from '../../../../Flux/Slice/Application/ApplicationSlice.js'
import { errorMessage, successMessage } from '../../../../Model/Message/IMessage.js'
import { useDispatch } from 'react-redux'
import type { Taxas } from '../../../../Model/ITaxa.js'

export interface PrimerNotFoundModalProps {
  primers: string[]
  amplicon: Taxas
}

export const PrimerNotFoundModal = (props: PrimerNotFoundModalProps & BaseModalProps) => {
  const [forwardSequence, setForwardSequence] = useState('')
  const [basePairLength, setBasePairLength] = useState(0)
  const [forwardName, setForwardName] = useState('')
  const [reverseSequence, setReverseSequence] = useState('')
  const [reverseName, setReverseName] = useState('')
  const [reference, setReference] = useState('')
  const [referenceLink, setReferenceLink] = useState('')

  const messagebox$ = useRef(new Subject<{ type: 'success' | 'error'; message: string }>()).current

  const labelClassName = `block text-gray-700 text-sm font-bold mb-2`
  const inputClassName = `shadow-sm appearance-none border rounded-sm w-full py-2 px-3 text-gray-700 mb-3 leading-tight focus:outline-hidden focus:shadow-outline`

  const dispatch = useDispatch()

  const handleSubmitAddPrimerMail = async (
    amplicon: string,
    args: {
      forwardName: string
      basePairLength: number
      forwardSequence: string
      reverseName: string
      reverseSequence: string
      reference: string
      referenceLink: string
    }
  ) => {
    try {
      await tracingAxios.post(`${Environment.API_URL}/v1/primer/`, { amplicon: amplicon, ...args }, { withCredentials: true })
      dispatch(addMessage(successMessage('Thank you, info successfully sent! We will contact you as soon as the new Primer is in our DB.')))
    } catch (error) {
      if (error !== undefined && error !== null && typeof error === 'object' && 'message' in error) {
        dispatch(addMessage(errorMessage('' + error.message)))
      } else {
        dispatch(addMessage(errorMessage('An unknown error occurred. Please try again later.')))
      }
    }
  }

  return (
    <BaseModal isOpen={props.isOpen} onClose={props.onClose}>
      <div className={'modal-box w-fit h-fit'}>
        <h3 className={'text-lg font-bold'}>Primer Not Found!</h3>
        <div>
          <div className="p-2">
            <p color={'error'}>
              We could not find the provided primers in our database. Please provide the base pair length (bp) of the amplicon to enable
              quality filtering. the following Primers were not found
            </p>
            {props.primers.reduce((a, b) => a + ', ' + b, '')}
          </div>
          <form>
            <label className={labelClassName}>Length of amplicon: *</label>
            <div className="w-1/2 flex justify-center items-center">
              <input
                type="number"
                value={basePairLength}
                placeholder={'Base pair length'}
                pattern="[^0-9]"
                onChange={(e) => setBasePairLength(Number(e.target.value))}
                className={inputClassName}
              />
              {/* <tooltip tooltip="Base Pair Length of amplicon">
                    <InformationCircleIcon className="h-8 w-8" />
                  </Tooltip> */}
            </div>
          </form>
          <div className="p-2">
            <p className={'text-font-error'}>
              If you do not want to reenter the base pair length of the used amplicon again next time, we recommend you to provide us with
              the additional information below and we will store this data to automatically find the base pair length next time.
            </p>
          </div>
          <div className="flex justify-between gap-2">
            <form className="grow">
              <label className={labelClassName}>Forward sequence: *</label>
              <input
                type="text"
                value={forwardSequence}
                placeholder={'Forward sequence'}
                pattern="[^A-Za-z]"
                onChange={(e) => setForwardSequence(e.target.value)}
                className={inputClassName}
              />
            </form>
            <form className="grow">
              <label className={labelClassName}>Forward name: *</label>
              <input
                type="text"
                value={forwardName}
                placeholder={'Forward name'}
                onChange={(e) => setForwardName(e.target.value)}
                className={inputClassName}
              />
            </form>
          </div>
          <div className="flex justify-between gap-2">
            <form className="grow">
              <label className={labelClassName}>Reverse sequence: *</label>
              <input
                type="text"
                value={reverseSequence}
                placeholder={'Reverse sequence'}
                pattern="[^A-Za-z]"
                onChange={(e) => setReverseSequence(e.target.value)}
                className={inputClassName}
              />
            </form>
            <form className="grow">
              <label className={labelClassName}>Reverse name: *</label>
              <input
                type="text"
                value={reverseName}
                placeholder={'Reverse name'}
                onChange={(e) => setReverseName(e.target.value)}
                className={inputClassName}
              />
            </form>
          </div>
          <div className="flex justify-between gap-2">
            <form className="grow">
              <label className={labelClassName}>Reference citation: *</label>
              <input
                type="text"
                value={reference}
                placeholder={'Reference citation'}
                onChange={(e) => setReference(e.target.value)}
                className={inputClassName}
              />
            </form>
            <form className="grow">
              <label className={labelClassName}>Reference DOI: *</label>
              <input
                type="text"
                value={referenceLink}
                placeholder={'Reference DOI'}
                onChange={(e) => setReferenceLink(e.target.value)}
                className={inputClassName}
              />
            </form>
          </div>
          {/* <Spacer /> */}
          <Messagebox messageInterval={1000000} message$={messagebox$}></Messagebox>
        </div>
        <div className="flex justify-between">
          <button role="button" className="btn btn-secondary justify-end" onClick={() => props.onClose()}>
            Close
          </button>
          <button
            role="button"
            className="btn btn-primary"
            onClick={async () => {
              await handleSubmitAddPrimerMail(props.amplicon, {
                forwardName,
                forwardSequence,
                reverseName,
                reverseSequence,
                reference,
                referenceLink,
                basePairLength
              })
              props.onClose()
            }}
          >
            <PaperAirplaneIcon className="w-4 h-4 -rotate-45" />
            Send Information
          </button>
        </div>
      </div>
    </BaseModal>
  )
}
